import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users, UserCircle, Repeat, Settings, LogOut, HomeIcon, Phone } from "lucide-react";
import { useMemo, useState } from "react";

const modules = [
  { label: "Inicio", path: "/home", icon: <HomeIcon size={18} />, roles: ["SUPERVISOR"] },
  { label: "Inicio", path: "/admin", icon: <HomeIcon size={18} />, roles: ["ADMIN"] },
  { label: "Inicio", path: "/operator", icon: <HomeIcon size={18} />, roles: ["OPERATOR"] },

  { label: "Candados", path: "/padlocks", icon: <Phone size={18} />, roles: ["SUPERVISOR"] },
  { label: "Usuarios", path: "/usuarios", icon: <UserCircle size={18} />, roles: ["SUPERVISOR"] },

  { label: "Usuarios", path: "/dashboardusersadmin", icon: <Users size={18} />, roles: ["ADMIN"] },
  { label: "Candados", path: "/locksadmin", icon: <Phone size={18} />, roles: ["ADMIN"] },
  { label: "Clientes", path: "/dashboardclientesadmin", icon: <Repeat size={18} />, roles: ["ADMIN"] },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const storedUser = localStorage.getItem("user");
  let user = null;
  try { user = storedUser ? JSON.parse(storedUser) : null; } catch {}

  const username = user?.name || user?.identification || "Sin usuario";
  const role = useMemo(() => {
    const raw = user?.role ?? user?.typeUser ?? user?.type ?? user?.rol ?? null;
    return raw ? String(raw).toUpperCase() : null;
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const filteredModules = useMemo(
    () => modules.filter((m) => !m.roles || m.roles.includes(role)),
    [role]
  );

  const handleNavClick = () => setOpen(false);

  return (
    <>
      {/* Botón hamburguesa móvil */}
      <button
        aria-label={open ? "Cerrar menú" : "Abrir menú"}
        onClick={() => setOpen((o) => !o)}
        className="lg:hidden fixed top-4 left-4 z-50 rounded-md p-2 bg-white/90 shadow"
      >
        <div className="relative h-5 w-7">
          <span className={["absolute left-0 top-0 h-[2px] w-7 bg-[#1D60F3] transition-transform duration-300", open ? "translate-y-[10px] rotate-45" : ""].join(" ")} />
          <span className={["absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-7 bg-[#1D60F3] transition-opacity duration-200", open ? "opacity-0" : "opacity-100"].join(" ")} />
          <span className={["absolute left-0 bottom-0 h-[2px] w-7 bg-[#1D60F3] transition-transform duration-300", open ? "-translate-y-[10px] -rotate-45" : ""].join(" ")} />
        </div>
      </button>

      {/* Overlay móvil */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={[
          "fixed left-0 top-0 z-50 w-64 bg-[#1D60F3] shadow-md p-4",
          "transition-transform duration-300",
          open ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
        ].join(" ")}
        style={{
          height: "100dvh",
          paddingTop: "env(safe-area-inset-top)",
          // Ojo: quitamos el paddingBottom aquí para ponerlo solo en el footer
        }}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="mb-6 flex justify-center shrink-0">
            <img
              src="/img/logoCloudtainerBlanco.svg"
              alt="Dealsys"
              className="max-w-[200px] object-contain"
            />
          </div>

          {/* Menú (scrollable) */}
          <nav className="flex-1 overflow-y-auto pr-1 -mr-1">
            <div className="flex flex-col gap-2">
              {filteredModules.map(({ label, path, icon }) => {
                const active = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={handleNavClick}
                    className={[
                      "flex items-center gap-2 text-m px-3 py-2 rounded-md transition-colors",
                      active
                        ? "bg-white text-[#1D60F3] font-semibold shadow"
                        : "text-white hover:bg-white hover:text-[#1D60F3]",
                    ].join(" ")}
                  >
                    {icon}
                    <span>{label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer con safe-area + 2px extra */}
          <div
            className="border-t border-white/30 pt-4 mt-4 shrink-0 bg-[#1D60F3]"
            style={{
              // añade 2px además del safe area inferior (si no hay safe area, quedan 2px)
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2px)",
            }}
          >
            <p className="text-white px-3 mb-2">USU: {username}</p>
            <p className="text-white px-3 mb-2">Rol: {role || "—"}</p>
            <Link
              to="/configuracion"
              onClick={handleNavClick}
              className="flex items-center gap-2 text-white px-3 py-2 mb-2 hover:bg-white hover:text-[#1D60F3] rounded-md transition"
            >
              <Settings size={18} />
              <span>Configuración</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors bg-transparent text-white hover:bg-red-500"
            >
              <LogOut size={18} />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
